import { Box, type SxProps, type Theme } from '@mui/material';
import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import { useCountAsOpenToolTray } from '~/components/toolbar/shared/ToolTrayPresenceProvider';

interface ToolHoverTrayProps {
    sx?: SxProps<Theme>;
    /** Set by a transition that wraps the tray, such as `Fade`. */
    style?: CSSProperties;
    'data-testid'?: string;
    children: ReactNode;
}

/**
 * A tool's options floating in a glassmorphic tray below the parent element,
 * over whatever is there, for options that show only while the tool is
 * hovered. They sit on one row unless `sx` lays them out otherwise. While
 * mounted it counts as an open tray of the enclosing
 * {@link ToolTrayPresenceProvider} scope.
 */
const ToolHoverTray = forwardRef<HTMLDivElement, ToolHoverTrayProps>(
    function ToolHoverTray(
        { sx: sxOverrides, style, 'data-testid': testId, children },
        ref
    ) {
        useCountAsOpenToolTray();
        return (
            <Box
                ref={ref}
                style={style}
                data-testid={testId}
                sx={[
                    (theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: '3px',
                        ...theme.mixins.floatingSurface,
                        background: 'rgba(4, 20, 32, 0.88)',
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: 100,
                        pointerEvents: 'auto',
                        whiteSpace: 'nowrap',
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        mt: 1,
                    }),
                    ...(Array.isArray(sxOverrides)
                        ? sxOverrides
                        : sxOverrides
                          ? [sxOverrides]
                          : []),
                ]}
            >
                {children}
            </Box>
        );
    }
);

export default ToolHoverTray;
